import { ClickAwayListener } from "@mui/material";
import type { ShopperAiTagGroupId } from "@redotech/effect-api-shared/ids/definitions";
import type {
  TagGroupResponse,
  TagResponse,
} from "@redotech/effect-api-shared/rpc/tag";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useTranslation } from "@redotech/redo-i18n";
import PlusIcon from "@redotech/redo-icons/plus.svg";
import { EffectApiClientContext } from "@redotech/redo-merchant-app-common/effect-api-client-provider";
import { PillTheme } from "@redotech/redo-storefront-common/pill-theme";
import { toast } from "@redotech/redo-web/alert";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { BaseRedoInput } from "@redotech/redo-web/arbiter-components/input/base-redo-text-input";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import {
  RedoList,
  type RedoListItem,
  type TextRedoListItem,
} from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Tooltip } from "@redotech/redo-web/arbiter-components/tooltip/tooltip";
import { Dropdown } from "@redotech/redo-web/dropdown";
import { Flex } from "@redotech/redo-web/flex";
import { ConversationTagPill as SupportTicketTagPill } from "@redotech/redo-web/support/conversation-tag-pill";
import { PillColorPicker } from "@redotech/redo-web/tags/tag-input-color-picker";
import { TagPillThemes } from "@redotech/redo-web/tags/tag-pill";
import { Text } from "@redotech/redo-web/text";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Effect } from "effect";
import { memo, useMemo, useRef, useState } from "react";
import {
  buildGroupedTagListItems,
  tagToBadgeProps,
} from "../../components/tag-list-utils";
import { catchApiErrors, catchApiErrorsSilent } from "../../db/error-handling";

// The macros settings page lives at /settings/.../macros, which is rendered
// outside `SupportProviders` (see support-v2/support-helpdesk.tsx). That
// means `useGlobalSupportTicketCollections` — the tanstack-db cache the
// support-helpdesk uses for tags — is unavailable here, so we fall back to
// a plain React Query fetch. If the settings tree is ever wrapped in
// `SupportProviders` we can replace this with `useTagSelector`.
const accountTagsKey = ["effect-api", "tag", "getAccountTags"] as const;

export interface MacroTagPickerProps {
  selectedTagNames: readonly string[];
  onChange(names: readonly string[]): void;
}

export const MacroTagPicker = memo(function MacroTagPicker({
  selectedTagNames,
  onChange,
}: MacroTagPickerProps) {
  const { t } = useTranslation();
  const effectApi = useRequiredContext(EffectApiClientContext);

  const { data, isLoading } = useQuery({
    queryKey: accountTagsKey,
    queryFn: async () => {
      const client = effectApi.getClient();
      return await effectApi.runPromise(
        catchApiErrorsSilent(client.tag.getAccountTags({})),
      );
    },
    staleTime: 30_000,
    meta: { showErrorToast: true },
  });

  const tags: readonly TagResponse[] = useMemo(
    () => data?.tags ?? [],
    [data?.tags],
  );
  const tagGroups: readonly TagGroupResponse[] = useMemo(
    () => data?.tagGroups ?? [],
    [data?.tagGroups],
  );

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [badgeRef, setBadgeRef] = useState<HTMLElement | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);

  const selectedTags = useMemo(
    () => tags.filter((tag) => selectedTagNames.includes(tag.name)),
    [tags, selectedTagNames],
  );
  const hasAvailableTags = tags.length > 0;

  const removeTagByName = useHandler((name: string) => {
    onChange(selectedTagNames.filter((n) => n !== name));
  });

  const tagGroupMap = useMemo(() => {
    const map = new Map<ShopperAiTagGroupId, TagGroupResponse>();
    for (const group of tagGroups) {
      map.set(group.id, group);
    }
    return map;
  }, [tagGroups]);

  const tagsByGroup = useMemo(() => {
    const map = new Map<ShopperAiTagGroupId, TagResponse[]>();
    for (const tag of tags) {
      const existing = map.get(tag.tagGroupId);
      if (existing) {
        existing.push(tag);
      } else {
        map.set(tag.tagGroupId, [tag]);
      }
    }
    return map;
  }, [tags]);

  const handleTagToggled = useHandler((tag: TagResponse) => {
    const currentSet = new Set(selectedTagNames);
    if (currentSet.has(tag.name)) {
      currentSet.delete(tag.name);
    } else {
      const group = tagGroupMap.get(tag.tagGroupId);
      // If the group does not allow multiple tags, remove any existing tags
      // from that group before adding the new one (matches V2 behaviour).
      if (group && !group.allowMultipleTags) {
        const groupTags = tagsByGroup.get(tag.tagGroupId) ?? [];
        for (const groupTag of groupTags) {
          currentSet.delete(groupTag.name);
        }
      }
      currentSet.add(tag.name);
    }
    onChange([...currentSet]);
  });

  const handleBadgeClick = useHandler(() => {
    setDropdownOpen((open) => !open);
  });

  const handleRequestCreateTag = useHandler(() => {
    setDropdownOpen(false);
    setCreateModalOpen(true);
  });

  const handleTagCreated = useHandler((tag: TagResponse) => {
    if (!selectedTagNames.includes(tag.name)) {
      onChange([...selectedTagNames, tag.name]);
    }
  });

  return (
    <Flex dir="column" gap="xs">
      <ClickAwayListener onClickAway={() => setDropdownOpen(false)}>
        <Flex
          align="center"
          gap="xxs"
          justify="flex-start"
          overflow="auto"
          wrap="wrap"
        >
          {selectedTags.map((tag) => (
            <SupportTicketTagPill
              key={tag.id}
              tag={{
                name: tag.name,
                pillTheme: tag.pillTheme ?? PillTheme.NORMAL,
              }}
              xClicked={() => removeTagByName(tag.name)}
            />
          ))}
          {hasAvailableTags ? (
            <Flex cursor="pointer" onClick={handleBadgeClick} ref={setBadgeRef}>
              <RedoBadge color="gray" shadow size="sm" text="+" />
            </Flex>
          ) : (
            <Tooltip
              title={
                isLoading ? t("Loading tags...") : t("No team tags available")
              }
            >
              <span style={{ opacity: 0.3 }}>
                <RedoBadge color="gray" shadow size="sm" text="+" />
              </span>
            </Tooltip>
          )}
          <Dropdown
            anchor={badgeRef}
            constrainHeight
            fitToAnchor={false}
            flexProps={{ p: "none" }}
            flip={false}
            open={dropdownOpen}
            placement="bottom-end"
          >
            <PickerDropdownContent
              onRequestCreateTag={handleRequestCreateTag}
              onTagToggled={handleTagToggled}
              selectedTagNames={selectedTagNames}
              tagGroups={tagGroups}
              tags={tags}
            />
          </Dropdown>
        </Flex>
      </ClickAwayListener>
      {createModalOpen && (
        <MacroCreateTagModal
          onClose={() => setCreateModalOpen(false)}
          onCreated={handleTagCreated}
          tagGroups={tagGroups}
          tags={tags}
        />
      )}
    </Flex>
  );
});

const PickerDropdownContent = memo(function PickerDropdownContent({
  tags,
  tagGroups,
  selectedTagNames,
  onTagToggled,
  onRequestCreateTag,
}: {
  tags: readonly TagResponse[];
  tagGroups: readonly TagGroupResponse[];
  selectedTagNames: readonly string[];
  onTagToggled(tag: TagResponse): void;
  onRequestCreateTag?: () => void;
}) {
  const { t } = useTranslation();
  const [searchText, setSearchText] = useState("");
  const [focusedIndex, setFocusedIndex] = useState<number | undefined>(
    undefined,
  );
  const containerRef = useRef<HTMLDivElement>(null);

  const filteredTags = useMemo(() => {
    if (!searchText) {
      return [...tags];
    }
    const lower = searchText.toLowerCase();
    return [...tags].filter((tag) => tag.name.toLowerCase().includes(lower));
  }, [tags, searchText]);

  const tagListItems: RedoListItem<TagResponse>[] = useMemo(() => {
    if (searchText) {
      return filteredTags.map((tag) => ({
        type: "badge" as const,
        id: tag.id,
        badge: tagToBadgeProps(tag),
        value: tag,
      }));
    }
    return buildGroupedTagListItems(filteredTags, tagGroups);
  }, [filteredTags, tagGroups, searchText]);

  const selectedTagIds = useMemo(
    () =>
      tags
        .filter((tag) => selectedTagNames.includes(tag.name))
        .map((tag) => tag.id),
    [tags, selectedTagNames],
  );

  return (
    <Flex dir="column" gap="none" ref={containerRef}>
      <Flex
        borderBottomWidth="1px"
        borderColor="primary"
        borderStyle="solid"
        px="lg"
        py="md"
      >
        <BaseRedoInput
          autoFocus
          placeholder={t("Search tags...")}
          setValue={setSearchText}
          size="sm"
          value={searchText}
        />
      </Flex>
      <Flex px="lg" py="md">
        {tagListItems.length === 0 ? (
          <Text fontSize="xs" textColor="secondary">
            {searchText ? t("No matching tags") : t("No tags available")}
          </Text>
        ) : (
          <RedoList
            focusedIndex={focusedIndex}
            items={tagListItems}
            itemSelected={({ item }) => {
              // RedoList raises selection for non-section items only; this
              // guard narrows the union so we can access `item.value`
              // without a type assertion.
              if (item.type === "section") {
                return;
              }
              if ("value" in item) {
                onTagToggled(item.value);
              }
            }}
            refToListenTo={containerRef.current}
            selectedItems={[...selectedTagIds]}
            selectionVariant="checkbox"
            setFocusedIndex={setFocusedIndex}
            size="sm"
          />
        )}
      </Flex>
      {onRequestCreateTag && (
        <Flex
          borderColor="primary"
          borderStyle="solid"
          borderTopWidth="1px"
          px="lg"
          py="sm"
        >
          <RedoButton
            hierarchy="tertiary"
            IconLeading={PlusIcon}
            onClick={onRequestCreateTag}
            size="xs"
            text={t("Create tag")}
          />
        </Flex>
      )}
    </Flex>
  );
});

const MacroCreateTagModal = memo(function MacroCreateTagModal({
  tags,
  tagGroups,
  onClose,
  onCreated,
}: {
  tags: readonly TagResponse[];
  tagGroups: readonly TagGroupResponse[];
  onClose: () => void;
  onCreated: (tag: TagResponse) => void;
}) {
  const effectApi = useRequiredContext(EffectApiClientContext);
  const queryClient = useQueryClient();

  const [name, setName] = useState("");
  const [pillTheme, setPillTheme] = useState<PillTheme>(
    PillTheme.PRIMARY_LIGHT,
  );
  const [selectedGroupId, setSelectedGroupId] = useState<
    ShopperAiTagGroupId | undefined
  >(undefined);

  const effectiveGroupId = selectedGroupId ?? tagGroups[0]?.id;

  const groupOptions = useMemo<TextRedoListItem<ShopperAiTagGroupId>[]>(
    () =>
      tagGroups.map((group) => ({
        id: group.id,
        type: "text" as const,
        text: group.name,
        value: group.id,
      })),
    [tagGroups],
  );

  const selectedGroupOption = useMemo(
    () => groupOptions.find((option) => option.value === effectiveGroupId),
    [groupOptions, effectiveGroupId],
  );

  const trimmedName = name.trim();

  const nameConflict = useMemo(
    () =>
      tags.some(
        (tag) =>
          tag.tagGroupId === effectiveGroupId &&
          tag.name.toLowerCase() === trimmedName.toLowerCase(),
      ),
    [tags, effectiveGroupId, trimmedName],
  );

  const mutation = useMutation<TagResponse, Error, void>({
    mutationFn: async () => {
      if (effectiveGroupId === undefined) {
        throw new Error("No tag group selected");
      }
      const client = effectApi.getClient();
      const { tag } = await effectApi.runPromise(
        catchApiErrors(
          client.tag
            .createTag({
              payload: {
                tagGroupId: effectiveGroupId,
                name: trimmedName,
                pillTheme,
              },
            })
            .pipe(
              Effect.catchTag("Conflict", () => {
                toast(
                  `A tag named "${trimmedName}" already exists in this group`,
                  { variant: "error" },
                );
                return Effect.die(new Error("Tag name conflict"));
              }),
            ),
        ),
      );
      return tag;
    },
    onSuccess: (tag) => {
      void queryClient.invalidateQueries({ queryKey: accountTagsKey });
      toast("Tag created", { variant: "success" });
      onCreated(tag);
      onClose();
    },
  });

  const canCreate =
    trimmedName.length > 0 &&
    effectiveGroupId !== undefined &&
    !nameConflict &&
    !mutation.isPending;

  return (
    <RedoModal
      isOpen
      onModalCloseRequested={onClose}
      primaryButton={{
        text: "Create",
        onClick: () => mutation.mutate(),
        disabled: !canCreate,
        pending: mutation.isPending,
      }}
      secondaryButton={{ text: "Cancel", onClick: onClose }}
      title="Create tag"
    >
      <Flex dir="column" gap="3xl" p="sm">
        <Flex dir="column" gap="xs">
          <RedoTextInput label="Tag name" setValue={setName} value={name} />
          {nameConflict && (
            <Text fontSize="xs" pl="sm" textColor="error">
              {`A tag named "${trimmedName}" already exists in this group`}
            </Text>
          )}
        </Flex>
        <Flex dir="column" gap="xs">
          <RedoSingleSelectDropdownInput
            label="Tag group"
            options={groupOptions}
            optionSelected={({ item }) => setSelectedGroupId(item.value)}
            placeholder="Select a group"
            selectedItem={selectedGroupOption}
            size="sm"
          />
          {tagGroups.length === 0 && (
            <Text fontSize="xs" pl="sm" textColor="secondary">
              Create a tag group in settings before adding tags.
            </Text>
          )}
        </Flex>
        <PillColorPicker
          label="Tag color"
          options={TagPillThemes}
          setValue={setPillTheme}
          value={pillTheme}
        />
      </Flex>
    </RedoModal>
  );
});
